import React, { useMemo } from 'react';
import { S14WhatToExpect, S14WhatToExpectProps, MilanGradientColors, MilanBackgroundColors } from '@milanlaser/mlcomponents';
import { processBackgroundAndGradientColorMap } from '../../utils/builder.utils';
import { PaddingSize, paddingMap } from '../../utils/builderPaddingOptions';

type ImageItem = {
  url: string;
  caption: string;
  altText: string;
};

type S14WhatToExpectBuilderProps = Omit<S14WhatToExpectProps, 'images' | 'imageCaptions' | 'imageAltTexts' | 'backgroundColor' | 'backgroundGradient'> & {
  desktopPaddingTop?: PaddingSize;
  desktopPaddingBottom?: PaddingSize;
  mobilePaddingTop?: PaddingSize;
  mobilePaddingBottom?: PaddingSize;
  images: ImageItem[];
  backgroundColor?: string;
  backgroundGradient?: MilanGradientColors | 'None' | 'default';
  backgroundImage?: string;
};

export function S14WhatToExpectSection(props: S14WhatToExpectBuilderProps) {
  const {
    images,
    desktopPaddingTop = 'large',
    desktopPaddingBottom = 'large',
    mobilePaddingTop = 'small',
    mobilePaddingBottom = 'small',
    backgroundColor,
    backgroundGradient,
    backgroundImage,
    ...restProps
  } = props;

  const topPaddingClasses = `mcl-pt-${paddingMap[mobilePaddingTop]} lg:mcl-pt-${paddingMap[desktopPaddingTop]}`;
  const bottomPaddingClasses = `mcl-pb-${paddingMap[mobilePaddingBottom]} lg:mcl-pb-${paddingMap[desktopPaddingBottom]}`;

  // Transform the images array into separate arrays for the component
  const imageUrls = useMemo(() => images?.map(img => img.url) || [], [images]);
  const imageCaptions = useMemo(() => images?.map(img => img.caption) || [], [images]);
  const imageAltTexts = useMemo(() => images?.map(img => img.altText) || [], [images]);

  const processedBackgroundColor = backgroundColor === 'None' ? 'White' : backgroundColor;
  const processedBackgroundGradient = backgroundGradient === 'None' ? 'default' : backgroundGradient;

  const modifiedProps = {
    ...restProps,
    images: imageUrls,
    imageCaptions,
    imageAltTexts,
    backgroundColor: (processedBackgroundColor || 'default') as MilanBackgroundColors,
    backgroundGradient: processedBackgroundGradient as MilanGradientColors,
    backgroundImage,
    // This removes the default bottom margin from the paragraphs that is being added by Bootstrap
    classNames: `mcl-mx-auto ${topPaddingClasses} ${bottomPaddingClasses} [&_p]:mb-0`,
  };

  const backgroundColorClass = processBackgroundAndGradientColorMap({
    backgroundColor: (processedBackgroundColor as MilanBackgroundColors) || 'White',
    gradientColor: processedBackgroundGradient || 'None',
  });

  return (
    <div className={`${backgroundColorClass}`}>
      <S14WhatToExpect {...modifiedProps} />
    </div>
  );
}
